/**
 * TTSPlayer - Real-time Text-to-Speech Player Component
 * Integrates with kani-tts-370m server for Chinese content reading
 */

import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Play, Pause, Square, Volume2, VolumeX, Loader2 } from 'lucide-react';

const TTSPlayer = ({ content, postTitle, language = 'zh' }) => {
  // State management
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [audioReady, setAudioReady] = useState(false);

  // Refs
  const audioContextRef = useRef(null);
  const audioSourceRef = useRef(null);
  const audioBufferRef = useRef(null);
  const startTimeRef = useRef(0);
  const pauseTimeRef = useRef(0);

  // TTS Server Configuration
  const TTS_SERVER_URL = 'http://localhost:8001';

  /**
   * Initialize Web Audio API
   */
  useEffect(() => {
    // Create AudioContext on component mount
    if (!audioContextRef.current) {
      try {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        console.log('✅ Web Audio API initialized');
      } catch (err) {
        console.error('❌ Web Audio API not supported:', err);
        setError('Audio playback not supported in this browser');
      }
    }

    // Cleanup on unmount
    return () => {
      if (audioSourceRef.current) {
        try {
          audioSourceRef.current.stop();
        } catch (err) {
          // Already stopped
        }
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  /**
   * Extract plain text from HTML content
   */
  const extractPlainText = (htmlContent) => {
    if (!htmlContent) return '';

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;

    // Remove script and style elements
    const scripts = tempDiv.getElementsByTagName('script');
    const styles = tempDiv.getElementsByTagName('style');
    [...scripts, ...styles].forEach(el => el.remove());

    // Get text content and clean up whitespace
    return tempDiv.textContent
      .replace(/\s+/g, ' ')
      .trim()
      .substring(0, 5000); // Limit to 5000 characters for TTS
  };

  /**
   * Generate audio from TTS server
   */
  const generateAudio = async () => {
    if (!content || !audioContextRef.current) return;

    setIsLoading(true);
    setError(null);

    try {
      const plainText = extractPlainText(content);
      if (!plainText) {
        throw new Error('No text content found');
      }

      console.log('🎤 Requesting TTS for:', plainText.substring(0, 100) + '...');

      // Request TTS from server
      const response = await fetch(`${TTS_SERVER_URL}/tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: plainText,
          language: language,
          speed: 1.0
        })
      });

      if (!response.ok) {
        throw new Error(`TTS server error: ${response.status}`);
      }

      // Get audio data
      const audioData = await response.arrayBuffer();

      // Decode audio
      const audioBuffer = await audioContextRef.current.decodeAudioData(audioData);
      audioBufferRef.current = audioBuffer;
      setAudioReady(true);

      console.log('✅ Audio generated successfully');
      console.log(`📊 Duration: ${audioBuffer.duration.toFixed(1)}s`);

    } catch (err) {
      console.error('❌ TTS generation failed:', err);
      setError(err.message || 'Failed to generate audio');
      setAudioReady(false);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Play audio
   */
  const playAudio = () => {
    if (!audioBufferRef.current || !audioContextRef.current) return;

    try {
      // Stop any currently playing audio
      if (audioSourceRef.current) {
        audioSourceRef.current.stop();
      }

      // Create new audio source
      const source = audioContextRef.current.createBufferSource();
      source.buffer = audioBufferRef.current;

      // Connect to destination (speakers)
      const gainNode = audioContextRef.current.createGain();
      gainNode.gain.value = isMuted ? 0 : 1;
      source.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);

      // Handle playback end
      source.onended = () => {
        setIsPlaying(false);
        setProgress(0);
        pauseTimeRef.current = 0;
      };

      // Calculate start offset for resume
      const offset = pauseTimeRef.current;
      source.start(0, offset);

      audioSourceRef.current = source;
      startTimeRef.current = audioContextRef.current.currentTime - offset;
      setIsPlaying(true);

      // Update progress
      updateProgress();

    } catch (err) {
      console.error('❌ Playback error:', err);
      setError('Playback failed');
    }
  };

  /**
   * Pause audio
   */
  const pauseAudio = () => {
    if (!audioSourceRef.current || !audioContextRef.current) return;

    try {
      const elapsed = audioContextRef.current.currentTime - startTimeRef.current;
      pauseTimeRef.current = elapsed;

      audioSourceRef.current.stop();
      audioSourceRef.current = null;
      setIsPlaying(false);

    } catch (err) {
      console.error('❌ Pause error:', err);
    }
  };

  /**
   * Stop audio
   */
  const stopAudio = () => {
    if (audioSourceRef.current) {
      try {
        audioSourceRef.current.stop();
      } catch (err) {
        // Already stopped
      }
      audioSourceRef.current = null;
    }

    setIsPlaying(false);
    setProgress(0);
    pauseTimeRef.current = 0;
  };

  /**
   * Update progress indicator
   */
  const updateProgress = () => {
    if (!isPlaying || !audioBufferRef.current || !audioContextRef.current) return;

    const elapsed = audioContextRef.current.currentTime - startTimeRef.current;
    const duration = audioBufferRef.current.duration;
    const progressPercent = (elapsed / duration) * 100;

    setProgress(Math.min(progressPercent, 100));

    if (elapsed < duration && isPlaying) {
      requestAnimationFrame(updateProgress);
    }
  };

  /**
   * Toggle play/pause
   */
  const handlePlayPause = () => {
    if (!audioReady) {
      generateAudio();
      return;
    }

    if (isPlaying) {
      pauseAudio();
    } else {
      playAudio();
    }
  };

  /**
   * Toggle mute
   */
  const handleMuteToggle = () => {
    setIsMuted(!isMuted);
    // Note: Would need to update gainNode in real-time for immediate effect
  };

  return (
    <motion.div
      id="tts-player-container"
      className="bg-gradient-to-r from-orange-50 to-red-50 dark:from-slate-900/50 dark:to-slate-800/50
                 backdrop-blur-sm rounded-xl p-4 lg:p-6 shadow-md mb-8"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <Volume2 className="w-6 h-6 text-orange-600 dark:text-orange-400" />
          <h3 className="font-heading text-lg font-semibold text-gray-900 dark:text-white">
            語音朗讀 (Text-to-Speech)
          </h3>
        </div>

        {/* Mute toggle */}
        <button
          onClick={handleMuteToggle}
          className="p-2 rounded-lg hover:bg-white/50 dark:hover:bg-slate-700/50 transition-colors"
          aria-label={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted ? (
            <VolumeX className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          ) : (
            <Volume2 className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          )}
        </button>
      </div>

      {/* Controls */}
      <div className="flex items-center space-x-4">
        {/* Play/Pause Button */}
        <button
          onClick={handlePlayPause}
          disabled={isLoading}
          className="flex items-center justify-center w-12 h-12 rounded-full
                   bg-gradient-to-br from-orange-600 to-red-600
                   hover:from-orange-700 hover:to-red-700
                   disabled:from-gray-400 disabled:to-gray-500
                   text-white shadow-lg transition-all duration-200
                   transform hover:scale-105 disabled:scale-100"
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          <AnimatePresence mode="wait">
            {isLoading ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0, rotate: 0 }}
                animate={{ opacity: 1, rotate: 360 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, rotate: { duration: 1, repeat: Infinity, ease: "linear" } }}
              >
                <Loader2 className="w-6 h-6" />
              </motion.div>
            ) : isPlaying ? (
              <motion.div
                key="pause"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
              >
                <Pause className="w-6 h-6" />
              </motion.div>
            ) : (
              <motion.div
                key="play"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
              >
                <Play className="w-6 h-6 ml-0.5" />
              </motion.div>
            )}
          </AnimatePresence>
        </button>

        {/* Stop Button */}
        <button
          onClick={stopAudio}
          disabled={!isPlaying && !audioReady}
          className="p-3 rounded-full bg-gray-200 dark:bg-slate-700
                   hover:bg-gray-300 dark:hover:bg-slate-600
                   disabled:opacity-50 disabled:cursor-not-allowed
                   text-gray-700 dark:text-gray-300 transition-colors"
          aria-label="Stop"
        >
          <Square className="w-5 h-5" />
        </button>

        {/* Progress Bar */}
        <div className="flex-1 h-2 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-orange-600 to-red-600 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.1 }}
          />
        </div>

        {/* Progress Percentage */}
        <span className="text-sm font-medium text-gray-600 dark:text-gray-400 min-w-[3rem] text-right">
          {progress.toFixed(0)}%
        </span>
      </div>

      {/* Error Display */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800
                     rounded-lg text-sm text-red-700 dark:text-red-400"
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Status Info */}
      <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
        {!audioReady && !isLoading && 'Click play to generate audio'}
        {audioReady && !isPlaying && 'Audio ready - click play to listen'}
        {isPlaying && 'Now playing...'}
      </div>
    </motion.div>
  );
};

export default TTSPlayer;
